import { FormEvent, SetStateAction } from "react"

import {
  useLazyGetCountriesQuery,
  useLazyGetParentCompaniesQuery,
  useLazyGetStatesQuery,
  useLazyGetZipCodesQuery,
} from "store/api/dictionaryApi"
import { UITypes } from "types"

import { getAddressLabelsByCountry } from "utils/addressUtils"
import ModalSectionTitle from "components/common/ModalSectionTitle/ModalSectionTitle"
import CitySuburbSelect from "components/form/CitySuburbSelect"
import { InputNew } from "components/form/InputNew"
import { SelectDynamic } from "components/form/SelectDynamic"

import styles from "./index.module.scss"

interface Props {
  inputValues: UITypes.Contractor
  error: {
    field: string
    message: string
  }
  onInputChange: (event: FormEvent<HTMLInputElement>) => void
  setInputValues: (value: SetStateAction<UITypes.Contractor>) => void
}

const DetailsSection = ({ inputValues, error, onInputChange, setInputValues }: Props) => {
  const addressLabels = getAddressLabelsByCountry(inputValues.country?.[0]?.label)

  const handleCountryChange = (country: UITypes.Option[]) => {
    if (inputValues.country?.[0]?.value === country[0]?.value) {
      return
    }

    setInputValues({ ...inputValues, ...{ country, zip: [], state: [], city: [], suburb: [] } })
  }

  const renderCitySuburb = () => {
    const showSuburb = inputValues.country?.[0]?.value === 3
    const entity = showSuburb ? "suburb" : "city"
    const values = showSuburb ? inputValues.suburb : inputValues.city

    return (
      <CitySuburbSelect
        showSuburb={showSuburb}
        zip_code={inputValues.zip?.[0]?.label || ""}
        country={inputValues.country?.[0]?.label}
        values={values || []}
        onChange={value => setInputValues({ ...inputValues, ...{ [entity]: value } })}
        error={error.field === entity ? error.message : ""}
      />
    )
  }

  return (
    <div>
      <ModalSectionTitle title="Details" />
      <div className={styles.row}>
        <InputNew
          label="Company name"
          placeholder="Enter company name"
          name="name"
          value={inputValues.name}
          className={styles.inputText}
          onChange={onInputChange}
          errorMessage={error.field === "name" ? error.message : ""}
        />
      </div>
      <div className={styles.row}>
        <InputNew
          label="Brand name"
          placeholder="Enter brand name"
          name="brand"
          value={inputValues.brand}
          className={styles.inputText}
          onChange={onInputChange}
          errorMessage={error.field === "brand" ? error.message : ""}
        />
        <SelectDynamic
          label="Parent company"
          placeholder="Select the parent company"
          searchFieldName="name"
          valueName="id"
          labelName="name"
          name="parent_company"
          values={inputValues.parentCompany || []}
          className={styles.inputText}
          useLazyQuery={useLazyGetParentCompaniesQuery}
          onChange={value => setInputValues({ ...inputValues, ...{ parentCompany: value } })}
          errorMessage={error.field === "parent_company" ? error.message : ""}
          queryParams={{ country_id: (inputValues.country?.[0]?.value || 1) as unknown as number }}
          backspaceDelete
        />
      </div>
      <div className={styles.row}>
        <InputNew
          label="Address 1"
          placeholder="Enter address "
          name="address1"
          value={inputValues.address1}
          className={styles.inputText}
          onChange={onInputChange}
          errorMessage={error.field === "address1" ? error.message : ""}
        />
        <InputNew
          label="Address 2"
          placeholder="Enter address "
          name="address2"
          value={inputValues.address2}
          className={styles.inputText}
          onChange={onInputChange}
          errorMessage={error.field === "address2" ? error.message : ""}
        />
      </div>
      <div className={styles.row}>
        <SelectDynamic
          label="Country"
          searchFieldName="country"
          valueName="id"
          labelName="name"
          name="country"
          values={inputValues.country || []}
          className={styles.inputText}
          useLazyQuery={useLazyGetCountriesQuery}
          placeholder="Enter Country"
          onChange={value => handleCountryChange(value)}
          errorMessage={error.field === "country" ? error.message : ""}
        />
        <SelectDynamic
          label={addressLabels.zip}
          searchFieldName="code"
          valueName="id"
          labelName="code"
          name="zip"
          values={inputValues.zip || []}
          className={styles.inputText}
          useLazyQuery={useLazyGetZipCodesQuery}
          placeholder={`Enter ${addressLabels.zip}`}
          onChange={value => setInputValues({ ...inputValues, ...{ zip: value } })}
          errorMessage={error.field === "zip" ? error.message : ""}
          queryParams={{ country_id: (inputValues.country?.[0]?.value || 1) as unknown as number }}
        />
      </div>
      <div className={styles.row}>
        <SelectDynamic
          label={addressLabels.state}
          placeholder={`Enter ${addressLabels.state}`}
          name="state"
          valueName="id"
          labelName="name"
          values={inputValues.state || []}
          className={styles.inputText}
          useLazyQuery={useLazyGetStatesQuery}
          queryParams={{ country_id: (inputValues.country?.[0]?.value || 1) as unknown as number }}
          onChange={value => setInputValues({ ...inputValues, ...{ state: value } })}
          errorMessage={error.field === "state" ? error.message : ""}
        />
        {renderCitySuburb()}
      </div>
    </div>
  )
}

export default DetailsSection
